import { useEffect, useMemo, useRef, type FC } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  DropdownBoundTemplate,
  DropdownTemplate,
  Expanded,
  ExpandedBlock,
  FieldsWrapper,
  GroupTitleTemplate,
  HtmlInfoTemplate,
  MultiselectBoundTemplate,
  MultiselectTemplate,
} from "@ryde/controls";
import {
  AssignDocksDuringProductionEnum,
  BulkProduceTypeEnum,
  DirectionEnum,
  DirectionsServiceEnum,
  getDefaultProductionTypes,
  ProductionTypeEnum,
  PROVIDER_OPTIONS,
  systemSettingsDtoAtom,
} from "@ryde/shared";

import {
  ConfigurationFormFields,
  configurationFormValidationRules,
} from "../configuration-form-validation";

export const BookingsProductionDrawerConfigurationForm: FC = () => {
  const { t } = useTranslation();
  const { getValues, setValue, watch } = useFormContext();
  const systemSettingsDto = useRecoilValue(systemSettingsDtoAtom);
  const isHubsLogicEnabled = Boolean(
    systemSettingsDto?.metadata?.enableHubsLogic,
  );
  const isDisableShiftsDropdown =
    systemSettingsDto?.metadata?.disableShiftsDropdown;
  const isDocksEnabled = Boolean(
    systemSettingsDto?.metadata?.allowDockStations,
  );
  const { defaultRideTypeForProduction } = systemSettingsDto?.metadata ?? {};
  const productionAction = getValues("productionAction");
  const assignDocksDuringProduction = watch(
    ConfigurationFormFields.assignDocksDuringProduction,
  );
  const isDocksExplanationVisible =
    isDocksEnabled &&
    !!assignDocksDuringProduction &&
    assignDocksDuringProduction !== AssignDocksDuringProductionEnum.NoDocks;
  const defaultProductionTypeAppliedRef = useRef(false);

  useEffect(() => {
    if (defaultRideTypeForProduction) {
      setValue(
        ConfigurationFormFields.rideTypeId,
        defaultRideTypeForProduction,
        {
          shouldDirty: false,
          shouldValidate: true,
        },
      );
    }
  }, [defaultRideTypeForProduction, setValue]);

  useEffect(() => {
    if (!systemSettingsDto || defaultProductionTypeAppliedRef.current) {
      return;
    }
    defaultProductionTypeAppliedRef.current = true;
    setValue(
      ConfigurationFormFields.productionType,
      getDefaultProductionTypes(isHubsLogicEnabled),
      { shouldDirty: false, shouldValidate: true },
    );
  }, [isHubsLogicEnabled, setValue, systemSettingsDto]);

  const directions = useMemo(
    () => [
      { value: DirectionEnum.PickUp, label: t("selectListLabel.DirA") },
      { value: DirectionEnum.DropOff, label: t("selectListLabel.DirB") },
      { value: DirectionEnum.Both, label: t("selectListLabel.DirAB") },
    ],
    [t],
  );

  const docksSelectItems = useMemo(
    () => [
      {
        value: AssignDocksDuringProductionEnum.NoDocks,
        label: t(`selectListLabel.${AssignDocksDuringProductionEnum.NoDocks}`),
      },
      {
        value: AssignDocksDuringProductionEnum.DropoffOnly,
        label: t(
          `selectListLabel.${AssignDocksDuringProductionEnum.DropoffOnly}`,
        ),
      },
    ],
    [t],
  );

  const productionTypeItems = useMemo(
    () => [
      {
        value: ProductionTypeEnum.Geo,
        label: t(`selectListLabel.${ProductionTypeEnum.Geo}`),
      },
      ...(isHubsLogicEnabled
        ? [
            {
              value: ProductionTypeEnum.Hub,
              label: t(`selectListLabel.${ProductionTypeEnum.Hub}`),
            },
          ]
        : []),
    ],
    [isHubsLogicEnabled, t],
  );

  return (
    <>
      <GroupTitleTemplate
        name="configurations_title"
        formLabel={t("produceButton.formLabel.configurations_title")}
      />

      <DropdownTemplate
        name={ConfigurationFormFields.productionAction}
        formLabel={t("produceButton.formLabel.productionAction")}
        formValidation={configurationFormValidationRules.productionAction}
        formAttributes={{
          selectItems: [
            {
              value: BulkProduceTypeEnum.Produce,
              label: t(`selectListLabel.${BulkProduceTypeEnum.Produce}`),
            },
            {
              value: BulkProduceTypeEnum.Join,
              label: t(`selectListLabel.${BulkProduceTypeEnum.Join}`),
            },
            {
              value: BulkProduceTypeEnum.Both,
              label: t(`selectListLabel.${BulkProduceTypeEnum.Both}`),
            },
          ],
          skipDropdownItemsTranslation: true,
        }}
      />
      {(productionAction === BulkProduceTypeEnum.Produce ||
        productionAction === BulkProduceTypeEnum.Both) && (
        <MultiselectTemplate
          name={ConfigurationFormFields.productionType}
          formLabel={t("produceButton.formLabel.productionType")}
          formValidation={configurationFormValidationRules.productionType}
          formAttributes={{
            selectItems: productionTypeItems,
            skipDropdownItemsTranslation: true,
          }}
          formVisibleCondition={{
            productionAction: {
              $in: [BulkProduceTypeEnum.Produce, BulkProduceTypeEnum.Both],
            },
          }}
        />
      )}
      <DropdownTemplate
        name={ConfigurationFormFields.direction}
        formLabel={t("produceButton.formLabel.direction")}
        formValidation={configurationFormValidationRules.direction}
        formAttributes={{
          selectItems: directions,
          skipDropdownItemsTranslation: true,
        }}
      />

      <GroupTitleTemplate
        name="ride_setting_title"
        formLabel={t("produceButton.formLabel.ride_setting_title")}
      />
      <DropdownBoundTemplate
        name={ConfigurationFormFields.rideTypeId}
        formLabel={t("produceButton.formLabel.rideTypeId")}
        formAttributes={{
          dropItemsSource: "rideType",
        }}
      />

      <DropdownTemplate
        name={ConfigurationFormFields.directionsService}
        formLabel={t("produceButton.formLabel.directionsService")}
        formValidation={configurationFormValidationRules.directionsService}
        formAttributes={{
          selectItems: PROVIDER_OPTIONS.filter(
            option => option.value !== DirectionsServiceEnum.TomTom,
          ),
        }}
      />

      <Expanded
        NavControl={
          <GroupTitleTemplate
            name="advanced"
            formLabel={t(`selectListLabel.advanced`)}
          />
        }
      >
        <ExpandedBlock>
          <FieldsWrapper>
            {!isDisableShiftsDropdown && (
              <MultiselectBoundTemplate
                name={ConfigurationFormFields.shiftIds}
                formLabel={t("produceButton.formLabel.shiftIds")}
                formValidation={configurationFormValidationRules.shiftIds}
                formAttributes={{
                  dropItemsSource: "shift",
                }}
              />
            )}

            <MultiselectBoundTemplate
              name={ConfigurationFormFields.areaIds}
              formLabel={t("produceButton.formLabel.areaIds")}
              formValidation={configurationFormValidationRules.areaIds}
              formAttributes={{
                dropItemsSource: "area",
              }}
            />
            <MultiselectBoundTemplate
              name={ConfigurationFormFields.siteIds}
              formLabel={t("produceButton.formLabel.siteIds")}
              formValidation={configurationFormValidationRules.siteIds}
              formAttributes={{
                dropItemsSource: "site",
              }}
            />
            <MultiselectBoundTemplate
              name={ConfigurationFormFields.unitIds}
              formLabel={t("produceButton.formLabel.unitIds")}
              formValidation={configurationFormValidationRules.unitIds}
              formAttributes={{
                dropItemsSource: "unit",
              }}
            />
            <DropdownBoundTemplate
              name={ConfigurationFormFields.passengerTypeId}
              formLabel={t("produceButton.formLabel.passengerTypeId")}
              formValidation={configurationFormValidationRules.passengerTypeId}
              formAttributes={{
                dropItemsSource: "passengerType",
              }}
            />
            {isDocksEnabled && (
              <DropdownTemplate
                name={ConfigurationFormFields.assignDocksDuringProduction}
                formLabel={t(
                  "produceButton.formLabel.assignDocksDuringProduction",
                )}
                formValidation={
                  configurationFormValidationRules.assignDocksDuringProduction
                }
                formAttributes={{
                  selectItems: docksSelectItems,
                  skipDropdownItemsTranslation: true,
                  tooltipTranslationKey:
                    "produceButton.tooltip.assignDocksDuringProduction",
                }}
              />
            )}
            {isDocksExplanationVisible && (
              <>
                <HtmlInfoTemplate
                  name="assignDocksDuringProductionNote"
                  formAttributes={{
                    descriptionTranslationKey:
                      "produceButton.formLabel.assignDocksDuringProductionNote",
                  }}
                />
                <HtmlInfoTemplate
                  name="assignDocksDuringProductionOverwriteNote"
                  formAttributes={{
                    descriptionTranslationKey:
                      "produceButton.formLabel.assignDocksDuringProductionOverwriteNote",
                  }}
                />
              </>
            )}
          </FieldsWrapper>
        </ExpandedBlock>
      </Expanded>
    </>
  );
};
